import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormProvider } from 'react-hook-form';
import { useBlocker, useLocation, useNavigate, useParams } from 'react-router';
import { LoadingScreen } from '@edusense/shared-ui-kit';
import { ConfirmModal } from '@edusense/shared-ui-components';
import { CourseEditTemplate, type CurriculumTabContentHandle } from '@edusense/widgets-course-edit-template';
import {
  useCourseEditLogic,
  BasicInfoForm,
  CurriculumTabContent,
  PricingTabContent,
  ResourcesTabContent,
  SettingsTabContent,
  PublishTabContent
} from '@edusense/features-course-edit';
import { useCurriculumStore } from '@edusense/entities-course';
import type { CourseDraftDetailResponse } from '@edusense/course-client';
import { useParaglideLocale } from '@edusense/shared-i18n';

import type { CourseEditPageProps } from './types';

const normalizeLanguageCode = (code?: string | null): string | null => {
  if (!code) {
    return null;
  }
  const trimmed = `${code}`.trim();
  if (!trimmed) {
    return null;
  }
  return trimmed.toLowerCase();
};

/**
 * 🏗️ FSD Pages Layer - Course Edit Page
 *
 * 기존 reference의 완전한 UI를 유지하면서 FSD 아키텍처로 재구성:
 * - Pages Layer: 라우팅과 페이지 구성 담당
 * - Template: CourseEditTemplate - 탭 네비게이션, 저장 상태, 게시 기능
 * - Features Layer: useCourseEditLogic, BasicInfoForm, CurriculumTabContent
 * - Core Layer: useCourseEditCore - 공통 비즈니스 로직
 */

const CourseEditPage: React.FC<CourseEditPageProps> = ({ className }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { courseId: routeCourseId } = useParams<{ courseId: string }>();
  const { currentLocale } = useParaglideLocale();
  const uiLanguage = useMemo(() => normalizeLanguageCode(currentLocale?.split('-')[0] ?? currentLocale) ?? 'ko', [currentLocale]);

  // FSD Features Layer - 비즈니스 로직 훅 사용
  const {
    state,
    actions,
    courseId,
    thumbnailInputRef,
    curriculumTabRef,
    formMethods,
    onSubmit,
    initialSections,
    initialLocalizedSections,
    currentBasicInfoLanguage,
    basicInfoAvailableLanguages,
    basicInfoTranslationSummary,
    changeBasicInfoLanguage,
    copyBasicInfoFromDefault,
    syncBasicInfoLanguageMetadata,
    courseData,
    resetAllChanges,
    resetSignal,
    hasUnsavedChanges,
    bypassNextBeforeUnload,
    autosaveRestoreModal,
    flushAutosaveSnapshot,
  } = useCourseEditLogic();

  const curriculumTabHandleRef = curriculumTabRef as unknown as React.RefObject<CurriculumTabContentHandle | null>;

  const { register, control, handleSubmit, formState: { errors, isDirty } } = formMethods;
  const isPublic = formMethods.watch('isPublic') ?? true;
  const allowComments = formMethods.watch('allowComments') ?? true;
  const watchedSupportedLanguages = formMethods.watch('supportedLanguages');
  const priceAmount = formMethods.watch('priceAmount') ?? 0;
  const priceCurrency = formMethods.watch('priceCurrency') ?? 'KRW';
  const isFreeCourse = formMethods.watch('isFree') ?? (priceAmount <= 0);
  const supportedLanguages = useMemo(() => {
    const merged = new Set<string>();
    (Array.isArray(watchedSupportedLanguages) ? watchedSupportedLanguages : watchedSupportedLanguages ? [watchedSupportedLanguages] : [])
      .forEach(code => code && merged.add(code));
    (Array.isArray(basicInfoAvailableLanguages) ? basicInfoAvailableLanguages : [])
      .forEach(code => code && merged.add(code));
    const formSupported = formMethods.getValues('supportedLanguages');
    (Array.isArray(formSupported) ? formSupported : formSupported ? [formSupported] : [])
      .forEach(code => code && merged.add(code));
    if (merged.size === 0) {
      merged.add('ko');
    }
    return Array.from(merged);
  }, [basicInfoAvailableLanguages, formMethods, watchedSupportedLanguages]);
  const watchedDefaultLanguage = formMethods.watch('defaultLanguage');
  const defaultLanguage = supportedLanguages.includes((watchedDefaultLanguage ?? '') as string)
    ? (watchedDefaultLanguage as string)
    : supportedLanguages[0] ?? 'ko';
  const supportedLanguagesKey = useMemo(
    () => (supportedLanguages || []).map(code => code ?? '').join('|'),
    [supportedLanguages],
  );
  const isCurriculumDirty = useCurriculumStore(state => state.isCurriculumDirty);
  const setCurriculumDirty = useCurriculumStore(state => state.setIsCurriculumDirty);
  const markCurriculumModified = useCurriculumStore(state => state.markAsModified);
  const cloneSections = useCallback((sections?: SectionData[] | null) => (
    (sections || []).map(section => ({
      ...section,
      lessons: (section.lessons || []).map(lesson => ({ ...lesson })),
    }))
  ), []);

  // 현재 탭 경로 추출
  const getCurrentTab = useCallback(() => {
    const pathSegments = location.pathname.split('/');
    const lastSegment = pathSegments[pathSegments.length - 1];
    const validTabs = ['edit', 'curriculum', 'pricing', 'resources', 'settings', 'publish'];

    return validTabs.includes(lastSegment)
      ? (lastSegment as 'edit' | 'curriculum' | 'pricing' | 'resources' | 'settings' | 'publish')
      : 'edit';
  }, [location.pathname]);

  const currentTab = getCurrentTab();

  const skipNextBlockRef = useRef(false);

  const requestNavigationBypass = useCallback(() => {
    skipNextBlockRef.current = true;
    bypassNextBeforeUnload();
    const targetCourseId = courseId || routeCourseId || 'new';
    try {
      sessionStorage.setItem(`course_edit_skip_autosave_prompt_${targetCourseId}`, '1');
    } catch (error) {
      // sessionStorage 사용 불가한 환경은 무시
    }
  }, [bypassNextBeforeUnload, courseId, routeCourseId]);

  // 저장 핸들러
  const handleSave = useCallback(async () => {
    if (courseId === 'new') {
      requestNavigationBypass();
    }
    await handleSubmit(onSubmit)();
  }, [courseId, handleSubmit, onSubmit, requestNavigationBypass]);

  // 게시 핸들러
  const handlePublish = useCallback(() => {
    if (courseId && courseId !== 'new') {
      actions.handlePublish();
    }
  }, [courseId, actions]);

  const handleReset = useCallback(() => {
    void resetAllChanges();
  }, [resetAllChanges]);

  // 커리큘럼 dirty 상태 변경 핸들러
  const handleCurriculumDirtyChange = useCallback((dirty: boolean) => {
    setCurriculumDirty(dirty);
    if (dirty) {
      markCurriculumModified();
    }
  }, [markCurriculumModified, setCurriculumDirty]);

  // 저장 상태 포맷
  const formatLastSaved = useCallback((timestamp: number | null | undefined) => {
    if (!timestamp) return '저장되지 않음';
    return `마지막 저장: ${new Date(timestamp).toLocaleTimeString()}`;
  }, []);

  const typedCourseData = courseData as CourseDraftDetailResponse | undefined;

  const lastSavedTimestamp = useMemo(() => {
    if (state.lastSavedAt) {
      return state.lastSavedAt;
    }
    const updatedAt = typedCourseData?.updatedAt ?? (typedCourseData as any)?.lastModifiedAt;
    if (updatedAt) {
      const parsed = Date.parse(updatedAt as string);
      return Number.isNaN(parsed) ? null : parsed;
    }
    return null;
  }, [state.lastSavedAt, typedCourseData]);

  const combinedDirty = useMemo(
    () => isDirty || isCurriculumDirty || hasUnsavedChanges,
    [isDirty, isCurriculumDirty, hasUnsavedChanges],
  );
  const blocker = useBlocker(combinedDirty);
  const [unsavedModalReason, setUnsavedModalReason] = useState<null | 'navigation' | 'reload'>(null);
  const pendingReloadActionRef = useRef<(() => void) | null>(null);
  const lastSavedMessage = formatLastSaved(lastSavedTimestamp);
  const isPublished = useMemo(
    () => typeof typedCourseData?.isPublished === 'boolean' ? typedCourseData.isPublished : false,
    [typedCourseData]
  );
  const serverUpdatedAt = useMemo(() => {
    if (!typedCourseData) {
      return null;
    }
    return typedCourseData.updatedAt ?? (typedCourseData as any)?.lastModifiedAt ?? null;
  }, [typedCourseData]);
  const currentCourseId = courseId || routeCourseId || 'new';
  const isCreating = currentCourseId === 'new';
  const previewLanguageCode = useMemo(
    () => normalizeLanguageCode(currentBasicInfoLanguage) ?? normalizeLanguageCode(defaultLanguage) ?? 'ko',
    [currentBasicInfoLanguage, defaultLanguage],
  );
  const previewDisabled = isCreating || !currentCourseId || currentCourseId === 'new';

  const handlePreview = useCallback(() => {
    if (previewDisabled) {
      return;
    }
    flushAutosaveSnapshot();

    const localeSegment = uiLanguage ?? 'ko';
    const languageParam = previewLanguageCode ?? 'ko';
    const previewUrl = new URL(window.location.origin);
    previewUrl.pathname = `/${localeSegment}/course/${currentCourseId}`;
    previewUrl.searchParams.set('preview', '1');
    if (languageParam) {
      previewUrl.searchParams.set('previewLanguage', languageParam);
    }

    window.open(previewUrl.toString(), '_blank', 'noopener');
  }, [previewDisabled, flushAutosaveSnapshot, uiLanguage, previewLanguageCode, currentCourseId]);
  const isInitialLoading = state.loading && !state.isSaving && !state.isPublishing;
  const unsavedModalOpen = unsavedModalReason !== null;

  useEffect(() => {
    if (blocker.state !== 'blocked') {
      return;
    }

    if (skipNextBlockRef.current) {
      skipNextBlockRef.current = false;
      bypassNextBeforeUnload();
      blocker.proceed();
      return;
    }

    if (unsavedModalReason === null) {
      setUnsavedModalReason('navigation');
    }
  }, [blocker, blocker.state, bypassNextBeforeUnload, unsavedModalReason]);

  useEffect(() => {
    if (!combinedDirty) {
      return;
    }

    const handleReloadShortcut = (event: KeyboardEvent) => {
      const isReloadKey = event.key === 'F5' || ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'r');
      if (!isReloadKey) {
        return;
      }
      if (unsavedModalReason !== null) {
        event.preventDefault();
        return;
      }

      event.preventDefault();
      pendingReloadActionRef.current = () => window.location.reload();
      setUnsavedModalReason('reload');
    };

    window.addEventListener('keydown', handleReloadShortcut);
    return () => window.removeEventListener('keydown', handleReloadShortcut);
  }, [combinedDirty, unsavedModalReason]);

  const handleConfirmUnsaved = useCallback(() => {
    flushAutosaveSnapshot();

    if (unsavedModalReason === 'reload') {
      const action = pendingReloadActionRef.current ?? (() => window.location.reload());
      pendingReloadActionRef.current = null;
      setUnsavedModalReason(null);
      bypassNextBeforeUnload();
      action();
      return;
    }

    if (unsavedModalReason === 'navigation') {
      setUnsavedModalReason(null);
      if (blocker.state === 'blocked') {
        blocker.proceed();
      }
      return;
    }

    setUnsavedModalReason(null);
  }, [unsavedModalReason, blocker, bypassNextBeforeUnload, flushAutosaveSnapshot]);

  const handleCancelUnsaved = useCallback(() => {
    if (unsavedModalReason === 'navigation' && blocker.state === 'blocked') {
      blocker.reset();
    }
    pendingReloadActionRef.current = null;
    setUnsavedModalReason(null);
  }, [unsavedModalReason, blocker]);

  const unsavedModalMessage = useMemo(() => {
    if (unsavedModalReason === 'reload') {
      return (
        <>
          <p>페이지를 새로고침하면 저장되지 않은 변경사항이 사라집니다.</p>
          <p>정말로 새로고침 하시겠습니까?</p>
        </>
      );
    }
    if (unsavedModalReason === 'navigation') {
      return (
        <>
          <p>다른 페이지로 이동하면 저장되지 않은 변경사항이 사라집니다.</p>
          <p>계속 진행하시겠습니까?</p>
        </>
      );
    }
    return null;
  }, [unsavedModalReason]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const globalObj = window as typeof window & {
      __courseEditTesting?: Record<string, unknown>;
    };

    globalObj.__courseEditTesting = {
      ...(globalObj.__courseEditTesting ?? {}),
      bypassUnsavedGuard: bypassNextBeforeUnload,
      bypassNavigationBlock: requestNavigationBypass,
      openNavigationGuardForTesting: () => {
        setUnsavedModalReason('navigation');
        return true;
      },
      getPublishDisabledForTesting: () => state.isPublishing || state.isSaving || hasUnsavedChanges || isCurriculumDirty || isPublished,
    };

    return () => {
      if (globalObj.__courseEditTesting?.bypassUnsavedGuard === bypassNextBeforeUnload) {
        delete globalObj.__courseEditTesting.bypassUnsavedGuard;
      }
      if (globalObj.__courseEditTesting?.bypassNavigationBlock === requestNavigationBypass) {
        delete globalObj.__courseEditTesting.bypassNavigationBlock;
      }
      if (globalObj.__courseEditTesting?.openNavigationGuardForTesting) {
        delete globalObj.__courseEditTesting.openNavigationGuardForTesting;
      }
      if (globalObj.__courseEditTesting?.getPublishDisabledForTesting) {
        delete globalObj.__courseEditTesting.getPublishDisabledForTesting;
      }
    };
  }, [bypassNextBeforeUnload, hasUnsavedChanges, isCurriculumDirty, isPublished, requestNavigationBypass, state.isPublishing, state.isSaving]);

  // Note: 저장 완료 후 상태 초기화는 useCourseEditLogic의 onSubmit에서 처리됨
  // 아래 useEffect는 중복 초기화를 방지하기 위해 제거하지 않고 유지
  useEffect(() => {
    if (!state.isSaving && state.lastSavedAt && isCurriculumDirty) {
      setCurriculumDirty(false);
    }
  }, [isCurriculumDirty, setCurriculumDirty, state.isSaving, state.lastSavedAt]);

  useEffect(() => {
    if (!state.isSaving && state.lastSavedAt) {
      const currentValues = formMethods.getValues();
      formMethods.reset(currentValues, { keepDefaultValues: false });
    }
  }, [formMethods, state.isSaving, state.lastSavedAt]);

  useEffect(() => {
    syncBasicInfoLanguageMetadata(supportedLanguages, defaultLanguage);
  }, [supportedLanguagesKey, defaultLanguage, syncBasicInfoLanguageMetadata, supportedLanguages]);

  useEffect(() => {
    if (currentTab !== 'curriculum') {
      return;
    }
    if (!courseId || courseId === 'new') {
      return;
    }

    const targetLanguage = normalizeLanguageCode(currentBasicInfoLanguage)
      ?? normalizeLanguageCode(defaultLanguage)
      ?? 'ko';
    const store = useCurriculumStore.getState();
    const normalizedCurrent = normalizeLanguageCode(store.currentLanguage);
    if (normalizedCurrent === targetLanguage) {
      return;
    }

    const snapshots = store.getLanguageSnapshots(courseId);
    const snapshotEntry = snapshots[targetLanguage] ?? snapshots.default;
    const normalizedTarget = targetLanguage;

    const fallbackSource = snapshotEntry?.sections
      ?? initialLocalizedSections?.[normalizedTarget]
      ?? (normalizedTarget === normalizeLanguageCode(defaultLanguage)
        ? initialSections ?? []
        : []);

    const fallbackSections = cloneSections(fallbackSource);

    const resolvedTimestamp = snapshotEntry?.lastModifiedLocallyTimestamp ?? (() => {
      if (typeof serverUpdatedAt === 'number') {
        return serverUpdatedAt;
      }
      if (!serverUpdatedAt) {
        return null;
      }
      const parsed = Date.parse(serverUpdatedAt as string);
      return Number.isNaN(parsed) ? null : parsed;
    })();

    store.switchLanguage(normalizedTarget, {
      courseId,
      fallbackSections,
      providedTimestamp: resolvedTimestamp ?? null,
    });
  }, [
    cloneSections,
    courseId,
    currentBasicInfoLanguage,
    currentTab,
    defaultLanguage,
    initialLocalizedSections,
    initialSections,
    serverUpdatedAt,
  ]);

  // 로딩 상태 처리
  if (isInitialLoading) {
    return <LoadingScreen />;
  }

  // 에러 상태 처리
  if (state.error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen">
        <h2 className="text-2xl font-bold text-red-600 mb-4">오류 발생</h2>
        <p className="text-gray-600">{state.error}</p>
        <button
          onClick={() => window.location.reload()}
          className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          다시 시도
        </button>
      </div>
    );
  }

  // 템플릿 props 준비
  const templateProps = {
    courseId: currentCourseId,
    courseTitle: formMethods.watch('title') || typedCourseData?.title || '',
    currentPath: currentTab,
    isDirty: combinedDirty,
    publishDisabled: state.isPublishing || isPublished, // dirty 상태 체크 제거 - 저장 완료 후 게시 가능하도록
    onCurriculumDirtyChange: handleCurriculumDirtyChange,
    onSave: handleSave,
    onReset: handleReset,
    onPublish: handlePublish,
    onBack: () => navigate(-1),
    isSaving: state.isSaving,
    lastSaved: lastSavedMessage,
    isCreating,
    isPublishing: state.isPublishing,
    isPublished,
    requestNavigationBypass,
    onPreview: handlePreview,
    previewDisabled,
    previewLanguage: previewLanguageCode,
  };

  return (
    <>
      <CourseEditTemplate {...templateProps}>
      {/* 기본 정보 탭 */}
      {currentTab === 'edit' && (
        <FormProvider {...formMethods}>
          <BasicInfoForm
            register={register}
            control={control}
            errors={errors}
            disabled={state.loading}
            saveLoading={state.loading}
            courseIdFromParent={courseId || routeCourseId}
            currentPreviewUrl={state.thumbnailState?.currentPreviewUrl || ''}
            thumbnailFileName={state.thumbnailState?.thumbnailFileName || ''}
            thumbnailActions={actions.thumbnailActions || {
              handleThumbnailChange: () => {},
              handleRemoveThumbnail: () => {},
              triggerThumbnailUpload: () => {}
            }}
            thumbnailInputRef={thumbnailInputRef}
            currentLanguage={currentBasicInfoLanguage}
            defaultLanguage={defaultLanguage}
            onLanguageChange={changeBasicInfoLanguage}
            onCopyFromDefault={copyBasicInfoFromDefault}
            translationSummary={basicInfoTranslationSummary}
            onSupportedLanguagesChange={(languages) => {
              const nextDefault = formMethods.getValues('defaultLanguage');
              syncBasicInfoLanguageMetadata(languages, nextDefault);
            }}
            onDefaultLanguageChange={(language) => {
              const languages = formMethods.getValues('supportedLanguages') ?? [];
              syncBasicInfoLanguageMetadata(languages, language);
            }}
            supportedLanguagesOverride={supportedLanguages}
          />
        </FormProvider>
      )}

      {/* 커리큘럼 탭 */}
      {currentTab === 'curriculum' && (
        courseId && courseId !== 'new' ? (
          <CurriculumTabContent
            key={resetSignal}
            ref={curriculumTabHandleRef}
            courseId={courseId}
            initialSections={initialSections || []}
            localizedInitialSections={initialLocalizedSections}
            availableLanguages={supportedLanguages}
            defaultLanguage={defaultLanguage}
            activeLanguage={currentBasicInfoLanguage}
            serverUpdatedAt={serverUpdatedAt}
            onDirtyStateChange={handleCurriculumDirtyChange}
          />
        ) : (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '400px',
            color: '#6b7280'
          }}>
            <h3 style={{ marginBottom: '1rem', color: '#374151' }}>커리큘럼을 편집하려면 먼저 강의를 저장하세요</h3>
            <p>기본 정보를 입력하고 저장한 후 커리큘럼을 추가할 수 있습니다.</p>
          </div>
        )
      )}

      {/* 가격 탭 */}
      {currentTab === 'pricing' && (
        <PricingTabContent
          courseId={courseId || routeCourseId || 'new'}
          disabled={state.loading}
          value={{
            priceAmount,
            priceCurrency,
            isFree: isFreeCourse,
          }}
          onPriceChange={(price) => {
            const amount = Number.isFinite(price.priceAmount) ? Math.max(0, price.priceAmount) : 0;
            formMethods.setValue('priceAmount', amount, { shouldDirty: true });
            formMethods.setValue('priceCurrency', price.priceCurrency ?? 'KRW', { shouldDirty: true });
            formMethods.setValue('isFree', price.isFree ?? amount <= 0, { shouldDirty: true });
          }}
        />
      )}

      {/* 리소스 탭 */}
      {currentTab === 'resources' && (
        <ResourcesTabContent
          courseId={courseId || routeCourseId || 'new'}
          disabled={state.loading}
        />
      )}

      {/* 설정 탭 */}
      {currentTab === 'settings' && (
        <SettingsTabContent
          courseId={courseId || routeCourseId || 'new'}
          disabled={state.loading}
          isPublic={isPublic}
          allowComments={allowComments}
          supportedLanguages={supportedLanguages}
          defaultLanguage={defaultLanguage}
          onSettingChange={(key, value) => {
            formMethods.setValue(key as any, value, { shouldDirty: true });
          }}
          onLanguagesChange={(languages) => {
            formMethods.setValue('supportedLanguages', languages, { shouldDirty: true });
          }}
          onDefaultLanguageChange={(language) => {
            formMethods.setValue('defaultLanguage', language, { shouldDirty: true });
          }}
        />
      )}

      {/* 게시 탭 */}
      {currentTab === 'publish' && (
        courseId && courseId !== 'new' ? (
          <PublishTabContent
            courseId={courseId}
            courseData={typedCourseData}
            disabled={state.loading}
            onPublishSuccess={() => {
              // 게시 성공 시 강의 페이지로 이동
              const localeSegment = uiLanguage ?? 'ko';
              const targetUrl = `/${localeSegment}/learn/course/${courseId}`;
              console.log('🎯🎯🎯 [CourseEditPage] onPublishSuccess called! Navigating to:', targetUrl);
              navigate(targetUrl);
            }}
          />
        ) : (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '400px',
            color: '#6b7280'
          }}>
            <h3 style={{ marginBottom: '1rem', color: '#374151' }}>강의를 게시하려면 먼저 저장하세요</h3>
            <p>기본 정보를 입력하고 저장한 후 게시할 수 있습니다.</p>
          </div>
        )
      )}
      </CourseEditTemplate>

      <ConfirmModal
        isOpen={unsavedModalOpen}
        title="저장되지 않은 변경사항"
        message={unsavedModalMessage}
        confirmText={unsavedModalReason === 'reload' ? '새로고침' : '이동하기'}
        cancelText="취소"
        isDangerous
        isDismissable={false}
        confirmColor="danger"
        cancelColor="primary"
        onConfirm={handleConfirmUnsaved}
        onCancel={handleCancelUnsaved}
      />

      {/* 임시저장 복원 모달 */}
      <ConfirmModal
        isOpen={autosaveRestoreModal.isOpen}
        title="임시저장 데이터 복원"
        message={
          <div>
            <p>저장되지 않은 임시 데이터가 있습니다.</p>
            <p className="text-sm text-gray-600 mt-1">
              이전에 작업하던 내용을 복원하시겠습니까?
            </p>
          </div>
        }
        confirmText="복원"
        cancelText="삭제"
        onConfirm={autosaveRestoreModal.onConfirm}
        onCancel={autosaveRestoreModal.onDiscard}
      />
    </>
  );
};

export default CourseEditPage;
